
import { useState } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../baseurl"; 
import { useToast } from "../admincomponents/AdminToast";

export default function AdminResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const emailFromState = location.state?.email || "";

  const [email, setEmail] = useState(emailFromState);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        variant: "error",
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
      });
      return;
    }

    if (password.length < 8) {
      toast({
        variant: "error",
        title: "Password too short",
        description: "Password must be at least 8 characters.",
      });
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${BASE_URL}/admin/reset-password`, {
        email,
        otp,
        newPassword: password,
      });

      toast({
        variant: "success",
        title: "Password reset",
        description: data.message || "Your password has been updated.",
      });
      setSubmitted(true);
      setTimeout(() => navigate("/admin/login"), 1200);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Something went wrong. Please try again.";

      toast({
        variant: "error",
        title: "Something went wrong",
        description: message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center px-6">
      <div className="w-full max-w-[420px] bg-white border border-gray-200 rounded-lg shadow-sm p-10">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-full bg-teal-500 flex items-center justify-center mb-4">
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              className="text-white"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 12a4 4 0 100-8 4 4 0 000 8zM4 20a8 8 0 0116 0"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1 className="text-gray-800 font-semibold text-[24px]">Reset Password</h1>
          <p className="text-gray-500 text-[14px] mt-1 text-center">
            {submitted
              ? "Your password has been reset"
              : "Enter the code and your new password"}
          </p>
        </div>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@futuraresearch.com"
                className="w-full bg-gray-50 border border-gray-300 rounded-md px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Reset Code
              </label>
              <input
                type="text"
                name="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                placeholder="Enter the code from your email"
                className="w-full bg-gray-50 border border-gray-300 rounded-md px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                New Password
              </label>
              <input
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full bg-gray-50 border border-gray-300 rounded-md px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-gray-600 text-sm font-medium mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full bg-gray-50 border border-gray-300 rounded-md px-4 py-3 text-gray-800 text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
              <p className="text-gray-400 text-xs mt-2">
                Must be at least 8 characters.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-md py-3 transition-colors"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        ) : (
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-teal-50 border border-teal-200 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-teal-600" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm">
              Your password has been successfully reset. Redirecting you to sign in...
            </p>
          </div>
        )}

        <p className="text-center text-gray-500 text-sm mt-6">
          Remembered it?{" "}
          <NavLink to="/admin/login" className="text-teal-600 hover:text-teal-700 font-medium">
            Back to sign in
          </NavLink>
        </p>
      </div>
    </div>
  );
}